/**
 * The rules for the people who sign in: a PIN good enough to be one, and a shop that always has
 * an owner.
 *
 * Kept out of the screen so they can be tested without one, and so the PIN you choose for
 * yourself in Ajustes and the one the owner gives a cashier are held to the same rules.
 */

import { isValidPinFormat, isWeakPin, verifyPin, type Role } from '@abastece/shared';

export interface Persona {
  id: string;
  nombre: string;
  rol: Role | string;
  pinHash: string;
  activo: boolean;
}

export type ProblemaPin = 'corto' | 'debil' | 'noCoincide';

/**
 * What is wrong with a PIN being chosen, or null. The repeat is judged only once something is
 * in it, so the form does not complain about the second box while the first is being typed.
 */
export function problemaDelPin(pin: string, repetido: string): ProblemaPin | null {
  if (!pin) return null;
  if (!isValidPinFormat(pin)) return 'corto';
  if (isWeakPin(pin)) return 'debil';
  if (repetido && repetido !== pin) return 'noCoincide';
  return null;
}

/**
 * Somebody else signs in with this PIN already.
 *
 * Two people on one PIN are one person to the app: the sign-in pad lets either in as whoever is
 * tapped, and a cashier whose PIN is also the owner's authorises their own voids — the owner's
 * PIN is checked against every owner, not against whoever is meant. Inactive people count too,
 * since they can be brought back. Hashes are salted, so this is a PBKDF2 per person: a shop's
 * handful, not a table scan.
 */
export async function pinEnUso(pin: string, personas: readonly Persona[], excepto?: string): Promise<boolean> {
  for (const p of personas) {
    if (p.id === excepto) continue;
    if (await verifyPin(pin, p.pinHash)) return true;
  }
  return false;
}

/**
 * Whether this person can stop being an owner — made a cashier, or deactivated — and leave the
 * shop with one. A shop with no active owner cannot add anybody, change a setting, or authorise
 * anything, and there is no one left to put it right.
 */
export function quedaOtroDueno(personas: readonly Persona[], id: string): boolean {
  return personas.some((p) => p.id !== id && p.activo && p.rol === 'dueno');
}

/** Nobody but the owner signs in yet: the first cashier is when voids start needing the owner. */
export const soloDuenos = (personas: readonly Persona[]): boolean =>
  personas.every((p) => p.rol === 'dueno');
